import { useCallback, useEffect, useState } from 'react';
import { viewServiceRequest } from '../api/viewServiceRequestApiService';
import { VIEW_SERVICE_REQUEST_LABELS as L } from '../constants/labels';
import type { ViewServiceRequestDetail } from '../types/viewServiceRequestType';

export const SR_NAV_IDS = {
  Summary: 'Podsumowanie',
  Facility: 'DanePlacowki',
  Equipment: 'DaneAparatury',
  Details: 'SzczegolyZalaczniki',
  Technician: 'DaneTechnika',
  AdminNotes: 'NotatkiAdministratora',
  History: 'Historia',
} as const;

export const SR_NAV_ITEMS = [
  {
    id: SR_NAV_IDS.Summary,
    label: L.NAV_SUMMARY,
    dataTestId: 'view-sr-nav-summary',
  },
  {
    id: SR_NAV_IDS.Facility,
    label: L.NAV_FACILITY,
    dataTestId: 'view-sr-nav-facility',
  },
  {
    id: SR_NAV_IDS.Equipment,
    label: L.NAV_EQUIPMENT,
    dataTestId: 'view-sr-nav-equipment',
  },
  {
    id: SR_NAV_IDS.Details,
    label: L.NAV_DETAILS,
    dataTestId: 'view-sr-nav-details',
  },
  {
    id: SR_NAV_IDS.Technician,
    label: L.NAV_TECHNICIAN,
    dataTestId: 'view-sr-nav-technician',
  },
  {
    id: SR_NAV_IDS.AdminNotes,
    label: L.NAV_ADMIN_NOTES,
    dataTestId: 'view-sr-nav-admin-notes',
  },
  {
    id: SR_NAV_IDS.History,
    label: L.NAV_HISTORY,
    dataTestId: 'view-sr-nav-history',
  },
] as const;

export interface UseViewServiceRequestReturn {
  detail: ViewServiceRequestDetail | undefined;
  isLoading: boolean;
  showErrorScreen: boolean;
  notFound: boolean;
  refetch: () => Promise<void>;
}

export const useViewServiceRequest = (
  serviceRequestId: string
): UseViewServiceRequestReturn => {
  const [detail, setDetail] = useState<ViewServiceRequestDetail | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState(true);
  const [showErrorScreen, setShowErrorScreen] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const fetchDetail = useCallback(async () => {
    setIsLoading(true);
    setNotFound(false);
    try {
      const response = await viewServiceRequest(serviceRequestId);
      if (response.success && response.data) {
        setDetail(response.data);
        setShowErrorScreen(false);
      } else if (response.notFound) {
        setNotFound(true);
        setShowErrorScreen(false);
      } else {
        setShowErrorScreen(true);
      }
    } catch {
      setShowErrorScreen(true);
    } finally {
      setIsLoading(false);
    }
  }, [serviceRequestId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  return {
    detail,
    isLoading,
    showErrorScreen,
    notFound,
    refetch: fetchDetail,
  };
};
